import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { fmt } from '../utils/format';
import Modal from '../components/Modal';
import WorkerSelect from '../components/WorkerSelect';
import AmountInput from '../components/AmountInput';

const TR_DAYS = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
const TR_MONTHS = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
function groupDate(s) {
  if (!s) return 'Tarihsiz';
  const d = new Date(s);
  return `${String(d.getDate()).padStart(2, '0')} ${TR_MONTHS[d.getMonth()]} ${d.getFullYear()}, ${TR_DAYS[d.getDay()]}`;
}
const todayStr = () => new Date().toISOString().split('T')[0];

const WORK_TYPES = ['Hasat', 'Ekim/Dikim', 'Budama', 'Sulama', 'İlaçlama', 'Çapalama', 'Gübreleme', 'Paketleme', 'Taşıma', 'Diğer'];
const PAYMENT_METHODS = [
  { value: 'cash', label: 'Nakit' }, { value: 'bank', label: 'Banka' },
  { value: 'credit', label: 'Kredi' }, { value: 'transfer', label: 'Havale/EFT' },
];
const PERIODS = [['today', 'Bugün'], ['week', 'Bu Hafta'], ['month', 'Bu Ay'], ['year', 'Bu Yıl'], ['', 'Tümü']];
const SOURCE_FILTERS = [['', 'Tümü'], ['labour', 'Hasat/Genel'], ['planting_labour', 'Ekim'], ['work_order_labour', 'İş Emri']];

// Effective paid — a row saved directly as 'paid' has no amount_paid recorded.
function effPaid(r) {
  const total = parseFloat(r.total_amount) || 0;
  if (r.payment_status === 'paid') return total;
  return Math.min(parseFloat(r.amount_paid) || 0, total);
}
function remaining(r) {
  const x = (parseFloat(r.total_amount) || 0) - effPaid(r);
  return x > 0 ? x : 0;
}

export default function YevmiyelerPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tab, setTab] = useState('day');           // 'day' | 'worker'
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({ totalAmount: 0, totalPaid: 0, totalPending: 0 });
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);

  // filters
  const [showFilters, setShowFilters] = useState(false);
  const [fPayment, setFPayment] = useState('');     // '' | 'paid' | 'pending'
  const [fSource, setFSource] = useState('');
  const [fWorkType, setFWorkType] = useState('');
  const [fWorker, setFWorker] = useState('');       // free-text search
  const [activeWorker, setActiveWorker] = useState(''); // worker card filter (exact name)

  // modals
  const [showAdd, setShowAdd] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const [bulkTarget, setBulkTarget] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (period) p.set('period', period);
    if (fPayment) p.set('paymentStatus', fPayment);
    if (fSource) p.set('sourceType', fSource);
    if (fWorkType) p.set('workType', fWorkType);
    if (activeWorker) p.set('workerName', activeWorker);
    else if (fWorker) p.set('workerName', fWorker);
    const qs = p.toString() ? `?${p}` : '';
    Promise.all([
      api.get(`/yevmiyeler${qs}`),
      api.get('/yevmiyeler/workers'),
    ]).then(([list, wk]) => {
      setData(list.data.data || []);
      setSummary(list.data.summary || { totalAmount: 0, totalPaid: 0, totalPending: 0 });
      setWorkers(wk.data.data || []);
    }).catch(e => {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Kayıtlar alınamadı');
    }).finally(() => setLoading(false));
  }, [period, fPayment, fSource, fWorkType, fWorker, activeWorker]);

  useEffect(() => { load(); }, [load]);

  const clearFilters = () => { setFPayment(''); setFSource(''); setFWorkType(''); setFWorker(''); setActiveWorker(''); };

  // group day list by work_date
  const grouped = useMemo(() => {
    const m = new Map();
    for (const r of data) {
      const key = r.work_date ? r.work_date.split('T')[0] : 'none';
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(r);
    }
    return [...m.entries()].sort((a, b) => (b[0] === 'none' ? -1 : a[0] === 'none' ? 1 : new Date(b[0]) - new Date(a[0])));
  }, [data]);

  const afterMutate = () => { setShowAdd(false); setEditTarget(null); setPayTarget(null); setBulkTarget(null); load(); };

  return (
    <div>
      <div className="card-row" style={{ marginBottom: 10 }}>
        <h2 className="section-title" style={{ margin: 0 }}>👷 {t('yevmiyeler.title')}</h2>
      </div>

      {/* view tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <button className={`chip${tab === 'day' ? ' active' : ''}`} onClick={() => setTab('day')}>📋 {t('yevmiyeler.byDay')}</button>
        <button className={`chip${tab === 'worker' ? ' active' : ''}`} onClick={() => setTab('worker')}>👤 {t('yevmiyeler.byWorker')}</button>
      </div>

      {/* period tabs */}
      <div className="period-tabs" style={{ marginBottom: 10 }}>
        {PERIODS.map(([p, label]) => (
          <button key={p || 'all'} className={`period-tab${period === p ? ' active' : ''}`} onClick={() => setPeriod(p)}>{label}</button>
        ))}
      </div>

      {/* summary cards */}
      <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, marginBottom: 10, scrollbarWidth: 'none' }}>
        <SummaryCard icon="👷" label={t('yevmiyeler.totalLabour')} value={fmt(summary.totalAmount)} color="#1c3a1c" />
        <SummaryCard icon="✅" label={t('yevmiyeler.paid')} value={fmt(summary.totalPaid)} color="#27ae60" />
        <SummaryCard icon="⏳" label={t('yevmiyeler.pending')} value={fmt(summary.totalPending)} color="#e67e22" />
      </div>

      {tab === 'day' && (
        <>
          {/* filter bar */}
          <div style={{ marginBottom: 10 }}>
            <button onClick={() => setShowFilters(s => !s)}
              style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 10, padding: '8px 12px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', color: '#2d5a27' }}>
              ⚙️ Filtrele {showFilters ? '▲' : '▼'}
              {(fPayment || fSource || fWorkType || fWorker || activeWorker) && <span style={{ marginLeft: 6, color: '#e67e22' }}>●</span>}
            </button>
            {showFilters && (
              <div className="card" style={{ marginTop: 8 }}>
                <FilterRow label="Ödeme">
                  {[['', 'Tümü'], ['paid', '✅ Ödendi'], ['pending', '⏳ Bekliyor']].map(([v, l]) => (
                    <button key={v} className={`chip${fPayment === v ? ' active' : ''}`} onClick={() => setFPayment(v)}>{l}</button>
                  ))}
                </FilterRow>
                <FilterRow label="Kaynak">
                  {SOURCE_FILTERS.map(([v, l]) => (
                    <button key={v} className={`chip${fSource === v ? ' active' : ''}`} onClick={() => setFSource(v)}>{l}</button>
                  ))}
                </FilterRow>
                <div className="form-row" style={{ marginTop: 4 }}>
                  <div className="form-group">
                    <label>İş Türü</label>
                    <input value={fWorkType} onChange={e => setFWorkType(e.target.value)} placeholder="ör. Hasat" />
                  </div>
                  <div className="form-group">
                    <label>İşçi</label>
                    <input value={fWorker} onChange={e => { setFWorker(e.target.value); setActiveWorker(''); }} placeholder="İsim ara" />
                  </div>
                </div>
                <button onClick={clearFilters}
                  style={{ background: 'none', border: 'none', color: '#e74c3c', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', padding: '4px 0' }}>
                  Filtreleri Temizle
                </button>
              </div>
            )}
          </div>

          {/* worker summary cards */}
          {workers.length > 0 && (
            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, marginBottom: 12, scrollbarWidth: 'none' }}>
              <button className={`chip${!activeWorker ? ' active' : ''}`} style={{ flexShrink: 0, alignSelf: 'flex-start' }}
                onClick={() => setActiveWorker('')}>Tümü</button>
              {workers.map(w => (
                <div key={w.workerName} onClick={() => { setActiveWorker(w.workerName === activeWorker ? '' : w.workerName); setFWorker(''); }}
                  className="card" style={{
                    minWidth: 150, flexShrink: 0, padding: '10px 12px', cursor: 'pointer',
                    border: activeWorker === w.workerName ? '2px solid var(--green-mid)' : '1px solid var(--border)',
                  }}>
                  <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>👤 {w.workerName}</div>
                  <div style={{ fontSize: '0.72rem', color: '#999', marginTop: 2 }}>{w.workCount} giriş · {Math.round(w.totalDays)} gün</div>
                  <div style={{ fontWeight: 800, fontSize: '0.82rem', color: '#2d5a27', marginTop: 4 }}>Toplam: {fmt(w.totalAmount)}</div>
                  {w.totalPending > 0 && <div style={{ fontSize: '0.72rem', color: '#e67e22', fontWeight: 700 }}>⏳ {fmt(w.totalPending)} bekliyor</div>}
                </div>
              ))}
            </div>
          )}

          {/* entries grouped by date */}
          {loading ? <div className="empty-state"><p>Yükleniyor...</p></div>
            : data.length === 0 ? (
              <div className="card empty-state" style={{ textAlign: 'center', padding: '28px 16px' }}>
                <div className="icon" style={{ fontSize: '2rem' }}>👷</div>
                <p style={{ marginTop: 6, color: '#7a9472' }}>Bu dönemde işçilik kaydı yok.</p>
                <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={() => setShowAdd(true)}>+ {t('yevmiyeler.addNew')}</button>
              </div>
            ) : grouped.map(([key, rows]) => {
              const dayTotal = rows.reduce((s, r) => s + (parseFloat(r.total_amount) || 0), 0);
              return (
                <div key={key} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '2px 2px 6px' }}>
                    <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#1c3a1c' }}>{key === 'none' ? 'Tarihsiz' : groupDate(key)}</span>
                    <span style={{ marginLeft: 'auto', fontWeight: 800, fontSize: '0.85rem', color: '#e67e22' }}>{fmt(dayTotal)}</span>
                  </div>
                  <div className="card" style={{ padding: 0 }}>
                    {rows.map(r => (
                      <LabourCard key={`${r.source_type}-${r.id}`} r={r}
                        onPay={() => setPayTarget(r)} onEdit={() => setEditTarget(r)} onDelete={() => handleDelete(r, load)}
                        onSource={() => openSource(r, navigate)} />
                    ))}
                  </div>
                </div>
              );
            })}
        </>
      )}

      {tab === 'worker' && (
        <>
          {loading ? <div className="empty-state"><p>Yükleniyor...</p></div>
            : workers.length === 0 ? (
              <div className="card empty-state" style={{ textAlign: 'center', padding: '28px 16px' }}>
                <div className="icon" style={{ fontSize: '2rem' }}>👤</div>
                <p style={{ marginTop: 6, color: '#7a9472' }}>Henüz işçi kaydı yok.</p>
              </div>
            ) : workers.map(w => (
              <div key={w.workerName} className="card" style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>👤 {w.workerName}</div>
                  <button onClick={() => { setTab('day'); setActiveWorker(w.workerName); }}
                    style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--green-mid)', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
                    Tümü →
                  </button>
                </div>
                <div style={{ fontSize: '0.8rem', color: '#666', marginTop: 4 }}>
                  {w.workCount} giriş · {Math.round(w.totalDays)} gün · <b>{fmt(w.totalAmount)}</b> toplam
                </div>
                <div style={{ fontSize: '0.8rem', marginTop: 2 }}>
                  <span style={{ color: '#27ae60', fontWeight: 700 }}>✅ {fmt(w.totalPaid)} ödendi</span>
                  {'  ·  '}
                  <span style={{ color: '#e67e22', fontWeight: 700 }}>⏳ {fmt(w.totalPending)} bekliyor</span>
                </div>
                {w.lastWorkDate && <div style={{ fontSize: '0.74rem', color: '#aaa', marginTop: 4 }}>Son iş: {groupDate(w.lastWorkDate.split('T')[0])}</div>}
                {w.totalPending > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <button className="btn btn-primary" style={{ padding: '6px 12px', fontSize: '0.82rem' }}
                      onClick={() => setBulkTarget(w)}>💰 {t('yevmiyeler.payAllPending')}</button>
                  </div>
                )}
              </div>
            ))}
        </>
      )}

      <button className="btn-fab" onClick={() => setShowAdd(true)}>+</button>

      {(showAdd || editTarget) && (
        <AddLabourModal target={editTarget} onClose={() => { setShowAdd(false); setEditTarget(null); }} onSaved={afterMutate} />
      )}
      {payTarget && <PayModal row={payTarget} onClose={() => setPayTarget(null)} onSaved={afterMutate} />}
      {bulkTarget && <BulkPayModal worker={bulkTarget} onClose={() => setBulkTarget(null)} onSaved={afterMutate} />}
    </div>
  );
}

// ─── helpers ─────────────────────────────────────────────────────────────────
async function handleDelete(r, reload) {
  if (!window.confirm(`"${r.worker_name}" işçilik kaydı silinsin mi?`)) return;
  try {
    await api.delete(`/yevmiyeler/${r.id}?sourceType=${r.source_type}`);
    toast.success('Silindi');
    reload();
  } catch (e) { toast.error(e?.response?.data?.detail || 'Silinemedi'); }
}
function openSource(r, navigate) {
  if (r.source_type === 'planting_labour' && r.source_id) navigate(`/plantings/${r.source_id}`);
  else if (r.source_type === 'work_order_labour') navigate('/work-orders');
  else if (r.source_type === 'labour' && r.source_id) navigate('/harvest');
}

function SummaryCard({ icon, label, value, color }) {
  return (
    <div className="card" style={{ minWidth: 130, flexShrink: 0, padding: '10px 12px' }}>
      <div style={{ fontSize: '0.78rem', color: '#888' }}>{icon} {label}</div>
      <div style={{ fontWeight: 800, fontSize: '1.05rem', color, marginTop: 4 }}>{value}</div>
    </div>
  );
}

function FilterRow({ label, children }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: '0.72rem', color: '#999', fontWeight: 700, marginBottom: 4 }}>{label}</div>
      <div className="chip-group" style={{ flexWrap: 'wrap' }}>{children}</div>
    </div>
  );
}

function LabourCard({ r, onPay, onEdit, onDelete, onSource }) {
  const paid = r.payment_status === 'paid';
  const rem = remaining(r);
  return (
    <div style={{ padding: '10px 12px', borderBottom: '1px solid #f4f4f4' }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>
            👤 {r.worker_name}
            <span style={{ marginLeft: 8, fontSize: '0.68rem', fontWeight: 700, padding: '2px 7px', borderRadius: 6, color: '#fff', background: paid ? '#27ae60' : '#e67e22' }}>
              {paid ? 'Ödendi' : r.payment_status === 'partial' ? 'Kısmi' : 'Bekliyor'}
            </span>
          </div>
          <div style={{ fontSize: '0.78rem', color: '#666', marginTop: 3 }}>
            {r.work_type || 'İşçilik'} · {r.num_workers || 1} kişi × {Number(r.days) || 0} gün
          </div>
          {r.source_label !== 'Genel' && r.source_detail && (
            <div onClick={onSource} style={{ fontSize: '0.76rem', color: 'var(--green-mid)', marginTop: 3, cursor: 'pointer', fontWeight: 600 }}>
              📎 {r.source_label}: {r.source_detail}
            </div>
          )}
          {parseFloat(r.daily_rate) > 0 && (
            <div style={{ fontSize: '0.72rem', color: '#aaa', marginTop: 2 }}>
              {fmt(r.daily_rate)}/gün × {Number(r.days) || 0} gün × {r.num_workers || 1} kişi
            </div>
          )}
          {r.payment_status === 'partial' && <div style={{ fontSize: '0.72rem', color: '#e67e22', marginTop: 2 }}>Kalan: {fmt(rem)}</div>}
        </div>
        <div style={{ fontWeight: 800, fontSize: '1rem', color: '#1c3a1c', whiteSpace: 'nowrap' }}>{fmt(r.total_amount)}</div>
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
        {!paid && <SmallBtn bg="#27ae60" onClick={onPay}>💰 Ödeme Yap</SmallBtn>}
        <SmallBtn bg="#607d8b" onClick={onEdit}>✏️ Düzenle</SmallBtn>
        <SmallBtn bg="#e74c3c" onClick={onDelete}>🗑️ Sil</SmallBtn>
      </div>
    </div>
  );
}

function SmallBtn({ bg, onClick, children }) {
  return (
    <button onClick={onClick} style={{ background: bg, color: '#fff', border: 'none', borderRadius: 8, padding: '5px 10px', fontSize: '0.74rem', fontWeight: 700, cursor: 'pointer' }}>
      {children}
    </button>
  );
}

// ─── Pay a single entry ───────────────────────────────────────────────────────
function PayModal({ row, onClose, onSaved }) {
  const rem = remaining(row);
  const [amount, setAmount] = useState(rem > 0 ? String(rem) : '');
  const [method, setMethod] = useState('cash');
  const [date, setDate] = useState(todayStr());
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) return toast.error('Geçerli bir tutar girin');
    setSaving(true);
    try {
      await api.put(`/yevmiyeler/${row.id}/pay?sourceType=${row.source_type}`, {
        paidAmount: amount, paymentMethod: method, paymentDate: date, notes,
      });
      toast.success('Ödeme kaydedildi ✓');
      onSaved();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Ödeme kaydedilemedi');
      setSaving(false);
    }
  };

  const over = parseFloat(amount) > rem;
  return (
    <Modal title={`💰 Ödeme · ${row.worker_name}`} onClose={onClose}>
      <form onSubmit={submit}>
        <div style={{ fontSize: '0.82rem', color: '#666', marginBottom: 8 }}>
          Toplam: <b>{fmt(row.total_amount)}</b> · Kalan: <b style={{ color: '#e67e22' }}>{fmt(rem)}</b>
        </div>
        <div className="form-group">
          <label>Tutar (₺) *</label>
          <AmountInput value={amount} onChange={setAmount} required autoFocus />
          {over && <div style={{ fontSize: '0.72rem', color: '#e67e22', marginTop: 4 }}>⚠️ Kalan tutardan fazla ödeme giriyorsunuz.</div>}
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Yöntem</label>
            <select value={method} onChange={e => setMethod(e.target.value)}>
              {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Tarih</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} />
          </div>
        </div>
        <div className="form-group">
          <label>Not</label>
          <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="İsteğe bağlı" />
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <button type="button" className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }} onClick={onClose}>İptal</button>
          <button type="submit" className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }} disabled={saving}>
            {saving ? 'Kaydediliyor...' : `💾 ${fmt(amount || 0)} Öde`}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Pay all pending entries of a worker (oldest-first, one payment each) ──────
function BulkPayModal({ worker, onClose, onSaved }) {
  const [amount, setAmount] = useState(String(worker.totalPending || ''));
  const [method, setMethod] = useState('cash');
  const [date, setDate] = useState(todayStr());
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState(null);

  useEffect(() => {
    // fetch this worker's open entries (oldest first) to allocate against
    api.get(`/yevmiyeler?workerName=${encodeURIComponent(worker.workerName)}&paymentStatus=pending`)
      .then(r => setItems((r.data.data || []).slice().sort((a, b) => new Date(a.work_date || 0) - new Date(b.work_date || 0))))
      .catch(() => setItems([]));
  }, [worker.workerName]);

  const submit = async (e) => {
    e.preventDefault();
    let left = parseFloat(amount);
    if (!(left > 0)) return toast.error('Geçerli bir tutar girin');
    if (!items || items.length === 0) return toast.error('Bekleyen kayıt bulunamadı');
    setSaving(true);
    try {
      for (const it of items) {
        if (left <= 0.005) break;
        const rem = remaining(it);
        if (rem <= 0) continue;
        const alloc = Math.round(Math.min(rem, left) * 100) / 100;
        if (alloc <= 0) continue;
        await api.put(`/yevmiyeler/${it.id}/pay?sourceType=${it.source_type}`, {
          paidAmount: alloc, paymentMethod: method, paymentDate: date, notes: `Toplu ödeme · ${worker.workerName}`,
        });
        left -= alloc;
      }
      toast.success('Toplu ödeme kaydedildi ✓');
      onSaved();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Toplu ödeme kaydedilemedi');
      setSaving(false);
    }
  };

  return (
    <Modal title={`💰 Toplu Ödeme · ${worker.workerName}`} onClose={onClose}>
      <form onSubmit={submit}>
        <div style={{ background: '#fef9e7', border: '1px solid #f0c040', borderRadius: 10, padding: 10, marginBottom: 10, fontSize: '0.85rem' }}>
          {worker.workerName}'in <b>{fmt(worker.totalPending)}</b> bekleyen ödemesi var.
          {items && <div style={{ fontSize: '0.75rem', color: '#999', marginTop: 4 }}>{items.length} açık kayıt · en eskiden başlayarak dağıtılır</div>}
        </div>
        <div className="form-group">
          <label>Ödenecek Tutar (₺) *</label>
          <AmountInput value={amount} onChange={setAmount} required />
          <button type="button" onClick={() => setAmount(String(worker.totalPending))}
            style={{ background: 'none', border: 'none', color: 'var(--green-mid)', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer', padding: '4px 0' }}>
            Tümü ({fmt(worker.totalPending)})
          </button>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Yöntem</label>
            <select value={method} onChange={e => setMethod(e.target.value)}>
              {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Tarih</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <button type="button" className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }} onClick={onClose}>İptal</button>
          <button type="submit" className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }} disabled={saving || !items}>
            {saving ? 'Kaydediliyor...' : `✅ ${fmt(amount || 0)} Öde`}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Add / edit labour, with optional source linkage ──────────────────────────
function AddLabourModal({ target, onClose, onSaved }) {
  const isEdit = !!target;
  const [form, setForm] = useState({
    workerName: target?.worker_name || '', farmWorkerId: target?.farm_worker_id || '',
    workType: target?.work_type || 'Hasat',
    numWorkers: target?.num_workers || 1, days: target?.days || 1,
    dailyRate: target?.daily_rate || '', totalAmount: target?.total_amount || '',
    paymentStatus: target?.payment_status === 'paid' ? 'paid' : 'pending',
    workDate: target?.work_date ? target.work_date.split('T')[0] : todayStr(),
    notes: target?.notes || '', manualTotal: false,
  });
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkType, setLinkType] = useState(''); // 'harvest'|'planting'|'work_order'
  const [linkId, setLinkId] = useState('');
  const [options, setOptions] = useState([]);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const autoTotal = (parseFloat(form.dailyRate) || 0) * (parseFloat(form.days) || 1) * (parseInt(form.numWorkers) || 1);
  const total = form.manualTotal && form.totalAmount !== '' ? parseFloat(form.totalAmount) : autoTotal;

  useEffect(() => {
    if (!linkType) { setOptions([]); return; }
    const url = linkType === 'harvest' ? '/farm/harvests' : linkType === 'planting' ? '/plantings' : '/work-orders';
    api.get(url).then(r => setOptions(r.data.data || [])).catch(() => setOptions([]));
  }, [linkType]);

  const optionLabel = (o) => {
    if (linkType === 'harvest') return `${o.product_name || 'Hasat'} - ${o.harvest_date ? new Date(o.harvest_date).toLocaleDateString('tr-TR') : ''}`;
    if (linkType === 'planting') return `${o.product_name || 'Ekim'}${o.variety ? ' - ' + o.variety : ''}${o.season_name ? ' · ' + o.season_name : ''}`;
    return `${o.title || 'İş Emri'}${o.field_name ? ' - ' + o.field_name : ''}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.workerName.trim()) return toast.error('İşçi adı girin');
    setSaving(true);
    const payload = {
      workerName: form.workerName, farmWorkerId: form.farmWorkerId || null,
      workType: form.workType, numWorkers: form.numWorkers, days: form.days,
      dailyRate: form.dailyRate, totalAmount: total, paymentStatus: form.paymentStatus,
      workDate: form.workDate, notes: form.notes,
    };
    try {
      if (isEdit) {
        await api.put(`/yevmiyeler/${target.id}?sourceType=${target.source_type}`, payload);
      } else {
        if (linkType === 'harvest' && linkId) payload.harvestId = linkId;
        if (linkType === 'planting' && linkId) payload.plantingId = linkId;
        if (linkType === 'work_order' && linkId) payload.workOrderId = linkId;
        await api.post('/yevmiyeler', payload);
      }
      toast.success(isEdit ? 'Güncellendi ✓' : 'Yevmiye kaydedildi ✓');
      onSaved();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Kaydedilemedi');
      setSaving(false);
    }
  };

  return (
    <Modal title={isEdit ? 'Yevmiye Düzenle' : 'Yeni Yevmiye'} onClose={onClose}>
      <form onSubmit={submit}>
        <WorkerSelect label="İşçi / Grup Adı" value={form.workerName} required
          onChange={v => { set('workerName', v); set('farmWorkerId', ''); }}
          onWorkerSelect={w => { set('workerName', `${w.first_name} ${w.last_name}`); set('farmWorkerId', w.id); if (w.default_daily_rate) set('dailyRate', w.default_daily_rate); }}
          placeholder="İsim veya grup adı" />

        <div className="form-group">
          <label>İş Türü</label>
          <select value={form.workType} onChange={e => set('workType', e.target.value)}>
            {WORK_TYPES.map(w => <option key={w} value={w}>{w}</option>)}
          </select>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Kişi Sayısı</label>
            <Stepper value={form.numWorkers} min={1} step={1} onChange={v => set('numWorkers', v)} />
          </div>
          <div className="form-group">
            <label>Gün Sayısı</label>
            <Stepper value={form.days} min={0.5} step={0.5} onChange={v => set('days', v)} />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Günlük Ücret (₺)</label>
            <AmountInput value={form.dailyRate} onChange={v => { set('dailyRate', v); set('manualTotal', false); }} />
          </div>
          <div className="form-group">
            <label>Toplam (₺)</label>
            <AmountInput value={form.manualTotal ? form.totalAmount : (autoTotal || '')} onChange={v => { set('totalAmount', v); set('manualTotal', true); }} />
          </div>
        </div>
        <div style={{ fontSize: '0.76rem', color: '#7a9472', marginBottom: 8 }}>
          {fmt(form.dailyRate || 0)}/gün × {Number(form.days) || 0} gün × {form.numWorkers || 1} kişi = <b>{fmt(total)}</b>
          {form.manualTotal && <button type="button" onClick={() => set('manualTotal', false)} style={{ marginLeft: 8, background: 'none', border: 'none', color: 'var(--green-mid)', fontWeight: 700, cursor: 'pointer', fontSize: '0.72rem' }}>otomatik</button>}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Tarih</label>
            <input type="date" value={form.workDate} onChange={e => set('workDate', e.target.value)} />
          </div>
          <div className="form-group">
            <label>Ödeme</label>
            <select value={form.paymentStatus} onChange={e => set('paymentStatus', e.target.value)}>
              <option value="paid">✅ Ödendi</option>
              <option value="pending">⏳ Bekliyor</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Not</label>
          <input value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="İsteğe bağlı" />
        </div>

        {/* optional source linkage — only when creating */}
        {!isEdit && (
          <div style={{ marginBottom: 8 }}>
            <button type="button" onClick={() => setLinkOpen(o => !o)}
              style={{ background: 'none', border: 'none', color: 'var(--green-mid)', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', padding: '4px 0' }}>
              🔗 Bu işçiliği bir olaya bağla {linkOpen ? '▲' : '▼'}
            </button>
            {linkOpen && (
              <div className="card" style={{ padding: 10 }}>
                <div className="chip-group" style={{ marginBottom: 8 }}>
                  {[['harvest', '🌾 Hasat'], ['planting', '🌱 Ekim'], ['work_order', '📋 İş Emri']].map(([v, l]) => (
                    <button key={v} type="button" className={`chip${linkType === v ? ' active' : ''}`}
                      onClick={() => { setLinkType(linkType === v ? '' : v); setLinkId(''); }}>{l}</button>
                  ))}
                </div>
                {linkType && (
                  <div className="form-group" style={{ margin: 0 }}>
                    <select value={linkId} onChange={e => setLinkId(e.target.value)}>
                      <option value="">Seçin (bağlamadan da kaydedebilirsiniz)</option>
                      {options.map(o => <option key={o.id} value={o.id}>{optionLabel(o)}</option>)}
                    </select>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <button type="button" className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }} onClick={onClose}>İptal</button>
          <button type="submit" className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }} disabled={saving}>
            {saving ? 'Kaydediliyor...' : '💾 Kaydet'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Stepper({ value, min = 0, step = 1, onChange }) {
  const btn = { width: 36, height: 38, border: '1px solid var(--border)', background: '#fff', borderRadius: 8, fontSize: '1.1rem', fontWeight: 700, cursor: 'pointer', color: '#2d5a27', flexShrink: 0 };
  const dec = () => onChange(Math.max(min, (parseFloat(value) || 0) - step));
  const inc = () => onChange((parseFloat(value) || 0) + step);
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <button type="button" style={btn} onClick={dec}>−</button>
      <input type="number" step={step} min={min} value={value} onChange={e => onChange(e.target.value)}
        style={{ textAlign: 'center', flex: 1, height: 38 }} />
      <button type="button" style={btn} onClick={inc}>+</button>
    </div>
  );
}
